import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calculator, BookOpen, List, Users, Truck, UtensilsCrossed, ChefHat, Scale, AlertTriangle, PieChart } from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cual es el food cost objetivo en catering?',
    a: 'El food cost objetivo de materia prima en catering esta entre el 25% y el 30% del precio por persona. Sin embargo, el coste total (incluyendo personal, logistica, material y transporte) se situa entre el 45% y el 55%. Esto deja un margen bruto del 45-55%, del cual hay que descontar costes fijos (local, administracion, seguros). Un catering rentable opera con un beneficio neto del 15-25% sobre facturacion.',
  },
  {
    q: '¿Como calculo el precio por persona en un catering?',
    a: 'La formula basica es: Precio por persona = Coste total por persona / (1 - Margen deseado). Por ejemplo, si tu coste total por persona (materia prima + personal + logistica + material) es de 42 euros y quieres un margen del 30%, el precio seria: 42 / (1 - 0,30) = 60 euros por persona. Siempre calcula primero el coste real y luego aplica el margen, nunca al reves.',
  },
  {
    q: '¿Cuantos camareros necesito por comensal en un catering?',
    a: 'La ratio estandar es 1 camarero por cada 15-20 comensales en servicio de mesa formal (bodas, galas). Para cocktail o buffet, 1 por cada 25-30 personas. Para eventos premium, 1 por cada 10-12 comensales. A esto hay que sumar personal de cocina: 1 cocinero por cada 30-40 comensales y 1 jefe de cocina por evento. El coste de personal suele representar el 25% del presupuesto total.',
  },
  {
    q: '¿Que porcentaje de sobreproduccion debo prever en catering?',
    a: 'La practica estandar es producir un 10-15% mas de lo necesario para cubrir imprevistos: roturas, platos que no salen perfectos, invitados adicionales de ultima hora o repeticiones. Este sobrecoste debe incluirse en el escandallo como merma planificada. Para aperitivos y canapes, la sobreproduccion puede ser del 15-20% porque el consumo es menos predecible.',
  },
  {
    q: '¿Como afecta la distancia al precio del catering?',
    a: 'La logistica puede representar entre el 5% y el 15% del coste total. Incluye: transporte refrigerado (0,80-1,50 euros/km), tiempo de desplazamiento del personal (horas extra), posible necesidad de alojamiento si el evento es lejos, y mayor riesgo de merma por transporte. Un evento a 100 km puede costar entre 300 y 800 euros mas solo en logistica. Siempre incluye estos costes en el presupuesto.',
  },
  {
    q: '¿Debo cobrar por el montaje y desmontaje?',
    a: 'Si, siempre. El montaje y desmontaje pueden suponer entre 4 y 8 horas de trabajo de equipo. Si no los cobras explicitamente, debes incluirlos en el precio por persona. La practica mas transparente es desglosarlos como partida separada en el presupuesto: "Montaje y desmontaje: X euros". Esto incluye montar la cocina temporal, preparar el servicio, recoger y limpiar despues del evento.',
  },
  {
    q: '¿Como escalo una receta de 4 a 100 raciones sin perder calidad?',
    a: 'No es una simple multiplicacion por 25. Al escalar, las especias y la sal se multiplican por un factor menor (x20-22 en vez de x25) porque su intensidad no es lineal. Los tiempos de coccion cambian. Las mermas pueden aumentar (mas manipulacion = mas perdida). Y necesitas equipamiento diferente: ollas industriales, hornos de conveccion, abatidores. Un software de escandallos como PLEKO aplica automaticamente estos factores de escala.',
  },
  {
    q: '¿Que diferencia hay entre el escandallo de un restaurante y el de catering?',
    a: 'La diferencia principal es que en catering debes incluir costes que en restaurante son fijos: transporte, montaje, alquiler de material (vajilla, cristaleria, mobiliario), personal eventual, imprevistos logisticos y sobreproduccion. Ademas, en catering trabajas con volumenes mucho mayores y el escalado de recetas no es proporcional. Un restaurante tiene food cost del 28-32% en materia prima; un catering, del 25-30% en materia prima pero del 45-55% en coste total.',
  },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Escandallo de Catering: Como Calcular Costes y Presupuestos para Eventos',
  description: 'Guia completa para calcular escandallos de catering: ejemplos reales de boda (100 pax), coffee break corporativo (50 pax) y coctel (80 pax). Desglose por partidas, costes ocultos y food cost objetivo.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' } },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    { '@type': 'ListItem', position: 3, name: 'Escandallo de Catering', item: 'https://pleko.app/escandallo-catering' },
  ],
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que-catering', label: 'Por que el catering necesita escandallos especificos' },
  { id: 'componentes-coste', label: 'Componentes del coste de un catering' },
  { id: 'ejemplo-boda', label: 'Ejemplo: escandallo de boda para 100 personas' },
  { id: 'ejemplo-coffee-break', label: 'Ejemplo: coffee break corporativo para 50 personas' },
  { id: 'ejemplo-coctel', label: 'Ejemplo: coctel para 80 personas' },
  { id: 'escalar-recetas', label: 'Como escalar recetas para grandes volumenes' },
  { id: 'costes-ocultos', label: 'Costes ocultos del catering' },
  { id: 'food-cost-objetivo', label: 'Food cost objetivo en catering' },
  { id: 'precio-persona', label: 'Como fijar el precio por persona con margen garantizado' },
  { id: 'software-vs-excel', label: 'Software vs Excel para catering' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function EscandalloCateringPage() {
  return (
    <>
      <Helmet>
        <title>Escandallo de Catering: Como Calcular Costes y Presupuestos para Eventos | PLEKO</title>
        <meta
          name="description"
          content="Guia completa para calcular escandallos de catering: ejemplos reales de boda (100 pax), coffee break (50 pax) y coctel (80 pax). Desglose por partidas, costes ocultos y food cost objetivo para eventos."
        />
        <meta
          name="keywords"
          content="escandallo catering, costes catering, presupuesto catering, food cost eventos, escandallo eventos, coste por persona catering, catering bodas coste, presupuesto catering boda, escandallo coctel, food cost catering, coste catering por persona, catering precio por cubierto"
        />
        <link rel="canonical" href="https://pleko.app/escandallo-catering" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/escandallo-catering" />
        <meta property="og:title" content="Escandallo de Catering: Como Calcular Costes y Presupuestos para Eventos" />
        <meta property="og:description" content="Guia completa para calcular escandallos de catering con 3 ejemplos reales. Desglose por partidas, costes ocultos y food cost objetivo." />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Escandallo de Catering — Costes y Presupuestos para Eventos | PLEKO" />
        <meta name="twitter:description" content="Calcula escandallos de catering con 3 ejemplos reales: boda, coffee break y coctel. Desglose por partidas y food cost objetivo." />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-indigo-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-6">
                <Users className="w-4 h-4" />
                Guia especializada para empresas de catering — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Escandallo de <span className="text-indigo-500">Catering</span>: Como Calcular Costes y Presupuestos para Eventos
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                El catering es el segmento de hosteleria donde un escandallo mal calculado puede convertir un evento rentable en una perdida de miles de euros. Aprende a calcular costes reales con 3 ejemplos completos y precios de 2026.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#ejemplo-boda"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors shadow-lg shadow-indigo-200"
                >
                  Ver ejemplo de boda (100 pax)
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Calculadora de costes
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-indigo-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">

            {/* Section 1: Por que el catering necesita escandallos especificos */}
            <h2 id="por-que-catering" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Por que el catering necesita escandallos especificos</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>escandallo de catering</strong> no es simplemente un <Link to="/escandallo" className="text-indigo-600 hover:underline">escandallo clasico de restaurante</Link> multiplicado por muchos comensales. El catering opera con variables que un restaurante convencional no tiene: <strong>volumen variable, logistica externa, personal eventual y alquiler de material</strong>. Ignorar cualquiera de estas partidas significa presupuestar a ciegas.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En un restaurante, muchos costes son fijos e invisibles (el horno ya esta ahi, el camarero ya esta contratado, la vajilla ya esta comprada). En catering, <strong>todo es variable</strong>: cada evento requiere calcular cuantos cocineros necesitas, cuantas furgonetas, cuantas mesas y sillas, y cuanto combustible para llegar al lugar del evento. Por eso, un escandallo de catering tiene entre 4 y 6 partidas de coste, frente a las 1-2 de un restaurante.
            </p>

            <h3 className="text-xl font-bold text-slate-800 mb-4">Las 3 diferencias clave frente al restaurante</h3>
            <ul className="space-y-3 mb-8">
              {[
                'Volumen variable — un dia cocinas para 30 personas, al siguiente para 300. Cada evento exige un escandallo propio',
                'Logistica externa — transporte refrigerado, montaje de cocina temporal, almacenamiento en destino. Costes que un restaurante no tiene',
                'Personal eventual — necesitas contratar camareros, cocineros y runners extra para cada evento, con sus costes de SS y gestion',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {/* Section 2: Componentes del coste */}
            <h2 id="componentes-coste" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Componentes del Coste de un Catering</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Mientras que en un restaurante el escandallo se centra en la materia prima, en catering el coste se divide en <strong>5 partidas principales</strong>. Conocerlas y cuantificarlas es la diferencia entre un presupuesto rentable y uno que te hace perder dinero.
            </p>

            {/* Desglose por partidas */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 mb-8">
              <p className="text-indigo-800 font-medium text-center text-lg mb-4">
                Desglose tipico del coste total en catering
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {[
                  { label: 'Materia prima', pct: '30%', color: 'text-indigo-600' },
                  { label: 'Personal', pct: '25%', color: 'text-blue-600' },
                  { label: 'Logistica', pct: '10%', color: 'text-amber-600' },
                  { label: 'Material', pct: '5%', color: 'text-green-600' },
                  { label: 'Margen', pct: '30%', color: 'text-slate-600' },
                ].map(({ label, pct, color }) => (
                  <div key={label} className="text-center">
                    <p className={`text-2xl font-bold ${color}`}>{pct}</p>
                    <p className="text-sm text-slate-600 mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-indigo-800 text-white">
                    <th className="p-3 text-left font-medium">Partida</th>
                    <th className="p-3 text-center font-medium">% del total</th>
                    <th className="p-3 text-left font-medium">Que incluye</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Materia prima', '25-30%', 'Ingredientes, bebidas, pan, cafe, agua, hielo. El nucleo del escandallo clasico.'],
                    ['Personal', '20-28%', 'Cocineros, camareros, runners, jefe de sala. Incluye SS, dietas y desplazamiento.'],
                    ['Logistica', '8-12%', 'Transporte refrigerado, combustible, peajes, tiempo de desplazamiento, montaje/desmontaje.'],
                    ['Material', '3-7%', 'Alquiler de vajilla, cristaleria, manteleria, mobiliario, decoracion floral.'],
                    ['Imprevistos', '3-5%', 'Sobreproduccion (10-15%), roturas, cambios de ultima hora, climatologia adversa.'],
                  ].map(([partida, pct, incluye], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{partida}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-indigo-700">{pct}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{incluye}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section 3: Ejemplo boda 100 personas */}
            <h2 id="ejemplo-boda" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><ChefHat className="w-7 h-7 text-indigo-500" /> Ejemplo Completo: Escandallo de Boda para 100 Personas</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Una boda en finca rural en Cataluna, junio 2026. Menu completo: cocktail de bienvenida (4 canapes + 2 bebidas), entrante, plato principal, postre, barra libre parcial. Veamos el <strong>escandallo completo de materia prima</strong> con precios de mayorista:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-indigo-800 text-white">
                    <th className="p-3 text-left font-medium">Concepto</th>
                    <th className="p-3 text-right font-medium">Cantidad (100 pax)</th>
                    <th className="p-3 text-left font-medium">Calculo</th>
                    <th className="p-3 text-right font-medium">Coste/pax</th>
                    <th className="p-3 text-right font-medium">Coste total</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Cocktail: Jamon iberico de cebo (loncha)', '3 kg', '30g/pax a 42 euros/kg', '1,26 euros', '126,00 euros'],
                    ['Cocktail: Tartaleta de salmon ahumado', '100 ud', '0,65 euros/ud', '0,65 euros', '65,00 euros'],
                    ['Cocktail: Brocheta de langostino', '100 ud', '1,20 euros/ud', '1,20 euros', '120,00 euros'],
                    ['Cocktail: Croqueta de jamon artesana', '200 ud', '0,55 euros/ud (2 por persona)', '1,10 euros', '110,00 euros'],
                    ['Cocktail: Cava y refrescos', '100 pax', '1 copa cava + 1 refresco', '1,80 euros', '180,00 euros'],
                    ['Entrante: Carpaccio de pulpo gallego', '12 kg brutos', '30% merma. 22 euros/kg', '3,43 euros', '343,20 euros'],
                    ['Entrante: Patata, pimenton, AOVE', '8 kg', '3,50 euros/kg con merma 15%', '0,32 euros', '32,20 euros'],
                    ['Principal: Solomillo de ternera', '18 kg', '18% merma. 38 euros/kg', '8,08 euros', '807,84 euros'],
                    ['Principal: Hojaldre, foie, duxelles setas', '100 ud', '3,80 euros/ud (5% merma)', '3,99 euros', '399,00 euros'],
                    ['Principal: Guarnicion (patata, esparrago)', '15 kg', '4,20 euros/kg con merma 15%', '0,72 euros', '72,45 euros'],
                    ['Principal: Salsa vino tinto y trufa', '5 litros', '12 euros/l (10% merma)', '0,66 euros', '66,00 euros'],
                    ['Postre: Tarta nupcial 3 pisos', '100 rac', '4,50 euros/rac (5% merma)', '4,73 euros', '472,50 euros'],
                    ['Postre: Sorbete de mango + petit fours', '100 ud', '1,80 euros/ud', '1,80 euros', '180,00 euros'],
                    ['Pan artesano (hogaza troceada)', '8 kg', '3,60 euros/kg', '0,29 euros', '28,80 euros'],
                    ['Agua, cafe e infusiones', '100 pax', '0,71 euros/pax', '0,71 euros', '71,00 euros'],
                    ['Sobreproduccion (+12%)', 'Global', '12% sobre materia prima', '3,53 euros', '353,16 euros'],
                  ].map(([concepto, cantidad, calculo, costePax, costeTotal], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{concepto}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cantidad}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{calculo}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-700">{costePax}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{costeTotal}</td>
                    </tr>
                  ))}
                  <tr className="bg-indigo-50">
                    <td colSpan={3} className="p-3 font-bold text-slate-800">COSTE MATERIA PRIMA TOTAL (100 pax)</td>
                    <td className="p-3 text-right font-bold text-indigo-600">34,27 euros/pax</td>
                    <td className="p-3 text-right font-bold text-indigo-600 text-lg">3.427,15 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Presupuesto completo con todas las partidas */}
            <h3 className="text-xl font-bold text-slate-800 mb-4">Presupuesto completo: todas las partidas</h3>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Partida</th>
                    <th className="p-3 text-right font-medium">Coste/pax</th>
                    <th className="p-3 text-right font-medium">Coste total</th>
                    <th className="p-3 text-center font-medium">% del PVP</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Materia prima (con sobreproduccion)', '34,27 euros', '3.427,15 euros', '30,7%', 'bg-indigo-50'],
                    ['Personal (6 camareros + 3 cocina + 1 jefe sala)', '24,00 euros', '2.400,00 euros', '21,5%', 'bg-blue-50'],
                    ['Logistica (transporte, montaje, desmontaje)', '8,50 euros', '850,00 euros', '7,6%', 'bg-amber-50'],
                    ['Material (vajilla, cristaleria, manteleria)', '6,50 euros', '650,00 euros', '5,8%', 'bg-green-50'],
                    ['Imprevistos (5%)', '3,66 euros', '366,36 euros', '3,3%', 'bg-red-50'],
                  ].map(([partida, costePax, costeTotal, pct, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{partida}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-700">{costePax}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{costeTotal}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{pct}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-100">
                    <td className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-slate-800">76,93 euros/pax</td>
                    <td className="p-3 text-right font-bold text-slate-800">7.693,51 euros</td>
                    <td className="p-3 text-center font-bold text-slate-800">68,9%</td>
                  </tr>
                  <tr className="bg-indigo-100">
                    <td className="p-3 font-bold text-indigo-800">PVP (precio por persona)</td>
                    <td className="p-3 text-right font-bold text-indigo-700">111,70 euros/pax</td>
                    <td className="p-3 text-right font-bold text-indigo-700 text-lg">11.170,00 euros</td>
                    <td className="p-3 text-center font-bold text-indigo-700">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste materia prima</p>
                <p className="text-2xl font-bold text-indigo-600">34,27 euros/pax</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-slate-800">76,93 euros/pax</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-blue-600">111,70 euros/pax</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Margen bruto</p>
                <p className="text-2xl font-bold text-green-600">31,1%</p>
                <p className="text-xs text-green-600 font-medium">3.476,49 euros</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                El solomillo Wellington es responsable del 35% del coste de materia prima. Si necesitas bajar el presupuesto sin reducir la percepcion de calidad, considera sustituir por lomo alto de ternera en Wellington: textura similar, un 30% mas barato, y la presentacion es identica. Otra opcion: reducir el cocktail a 3 canapes en vez de 4, ahorrando unos 110 euros.
              </p>
            </div>

            {/* Section 4: Ejemplo coffee break */}
            <h2 id="ejemplo-coffee-break" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><UtensilsCrossed className="w-7 h-7 text-amber-600" /> Ejemplo: Coffee Break Corporativo para 50 Personas</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Coffee break para una jornada corporativa en un hotel de Barcelona. Servicio de media manana con cafe, te, zumos, bolleria variada y fruta fresca. Duracion: 30 minutos. Es el formato de catering con <strong>mejor margen</strong> porque la materia prima es economica y el servicio es rapido.
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-amber-700 text-white">
                    <th className="p-3 text-left font-medium">Concepto</th>
                    <th className="p-3 text-right font-medium">Cantidad (50 pax)</th>
                    <th className="p-3 text-left font-medium">Calculo</th>
                    <th className="p-3 text-right font-medium">Coste/pax</th>
                    <th className="p-3 text-right font-medium">Coste total</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Cafe en grano (mezcla especialidad)', '350 g', '7g/cafe x 50. 18 euros/kg', '0,13 euros', '6,30 euros'],
                    ['Leche entera + leche vegetal', '10 L', '200ml/cafe. 1,15 euros/l', '0,23 euros', '11,50 euros'],
                    ['Te e infusiones (bolsitas premium)', '20 ud', '0,25 euros/ud (40% asistentes)', '0,10 euros', '5,00 euros'],
                    ['Zumo de naranja natural', '15 L', '300ml/pax. 2,80 euros/l', '0,84 euros', '42,00 euros'],
                    ['Agua mineral (botella 0,5L)', '50 ud', '0,35 euros/ud', '0,35 euros', '17,50 euros'],
                    ['Croissant de mantequilla', '50 ud', '0,38 euros/ud', '0,38 euros', '19,00 euros'],
                    ['Mini napolitana de chocolate', '50 ud', '0,42 euros/ud', '0,42 euros', '21,00 euros'],
                    ['Mini danish de crema', '50 ud', '0,40 euros/ud', '0,40 euros', '20,00 euros'],
                    ['Fruta fresca cortada (sandia, melon, uva)', '5 kg', '3,80 euros/kg con merma 25%', '0,38 euros', '19,00 euros'],
                    ['Azucar, edulcorante, servilletas', 'Lote', '0,05 euros/pax', '0,05 euros', '2,50 euros'],
                    ['Sobreproduccion (+10%)', 'Global', '10% sobre materia prima', '0,33 euros', '16,38 euros'],
                  ].map(([concepto, cantidad, calculo, costePax, costeTotal], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{concepto}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cantidad}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{calculo}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-700">{costePax}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{costeTotal}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50">
                    <td colSpan={3} className="p-3 font-bold text-slate-800">COSTE MATERIA PRIMA TOTAL (50 pax)</td>
                    <td className="p-3 text-right font-bold text-amber-700">3,61 euros/pax</td>
                    <td className="p-3 text-right font-bold text-amber-700 text-lg">180,18 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste materia prima</p>
                <p className="text-2xl font-bold text-amber-700">3,61 euros/pax</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total (+personal+log.)</p>
                <p className="text-2xl font-bold text-slate-800">7,20 euros/pax</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-blue-600">14,50 euros/pax</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost (MP)</p>
                <p className="text-2xl font-bold text-green-600">24,9%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Con un food cost de materia prima del 24,9%, el coffee break es el <strong>formato mas rentable del catering</strong>. El personal necesario es minimo (2 personas de servicio) y la logistica es sencilla. Un catering que haga 4 coffee breaks a la semana de 50 personas genera unos <strong>1.460 euros de margen bruto semanal</strong> con muy poco esfuerzo operativo. La clave: negociar precios de bolleria por volumen con el proveedor.
            </p>

            {/* Section 5: Ejemplo coctel */}
            <h2 id="ejemplo-coctel" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Users className="w-7 h-7 text-purple-500" /> Ejemplo: Coctel para 80 Personas</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Coctel para inauguracion de oficinas en Madrid. Formato standing con 8 pases de canapes (4 frios + 4 calientes), tabla de quesos e ibericos, barra de bebidas con cava, vino, cerveza y refrescos. Duracion: 2,5 horas. El coctel es el formato donde la <strong>sobreproduccion es mas critica</strong> porque el consumo individual es impredecible.
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-purple-800 text-white">
                    <th className="p-3 text-left font-medium">Concepto</th>
                    <th className="p-3 text-right font-medium">Cantidad (80 pax)</th>
                    <th className="p-3 text-left font-medium">Calculo</th>
                    <th className="p-3 text-right font-medium">Coste/pax</th>
                    <th className="p-3 text-right font-medium">Coste total</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Canape frio: Tosta de salmon ahumado', '160 ud', '2 por persona. 0,85 euros/ud', '1,70 euros', '136,00 euros'],
                    ['Canape frio: Tartar de atun en cucurucho', '80 ud', '1 por persona. 1,40 euros/ud', '1,40 euros', '112,00 euros'],
                    ['Canape frio: Brocheta caprese', '80 ud', '1 por persona. 0,55 euros/ud', '0,55 euros', '44,00 euros'],
                    ['Canape frio: Gazpacho en vasito', '80 ud', '1 por persona. 0,45 euros/ud', '0,45 euros', '36,00 euros'],
                    ['Canape caliente: Croqueta de jamon', '160 ud', '2 por persona. 0,55 euros/ud', '1,10 euros', '88,00 euros'],
                    ['Canape caliente: Mini quiche de verduras', '80 ud', '1 por persona. 0,60 euros/ud', '0,60 euros', '48,00 euros'],
                    ['Canape caliente: Brocheta de langostino', '80 ud', '1 por persona. 1,20 euros/ud', '1,20 euros', '96,00 euros'],
                    ['Canape caliente: Empanadilla de atun', '80 ud', '1 por persona. 0,50 euros/ud', '0,50 euros', '40,00 euros'],
                    ['Tabla ibericos y quesos', '6 kg', '75g/pax. 28 euros/kg medio', '2,10 euros', '168,00 euros'],
                    ['Cava (2 copas/pax)', '27 botellas', '6 copas/botella. 8,50 euros/bot', '2,87 euros', '229,50 euros'],
                    ['Vino tinto y blanco (1 copa/pax)', '14 botellas', '6 copas/botella. 7,00 euros/bot', '1,23 euros', '98,00 euros'],
                    ['Cerveza artesana', '40 ud', '50% asistentes. 1,80 euros/ud', '0,90 euros', '72,00 euros'],
                    ['Refrescos y agua', '80 pax', '0,60 euros/pax', '0,60 euros', '48,00 euros'],
                    ['Sobreproduccion (+15%)', 'Global', '15% sobre MP (cocktail = mas impredecible)', '2,37 euros', '189,23 euros'],
                  ].map(([concepto, cantidad, calculo, costePax, costeTotal], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{concepto}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cantidad}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{calculo}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-700">{costePax}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{costeTotal}</td>
                    </tr>
                  ))}
                  <tr className="bg-purple-50">
                    <td colSpan={3} className="p-3 font-bold text-slate-800">COSTE MATERIA PRIMA TOTAL (80 pax)</td>
                    <td className="p-3 text-right font-bold text-purple-600">19,94 euros/pax</td>
                    <td className="p-3 text-right font-bold text-purple-600 text-lg">1.594,73 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste materia prima</p>
                <p className="text-2xl font-bold text-purple-600">19,94 euros/pax</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total (+personal+log.)</p>
                <p className="text-2xl font-bold text-slate-800">38,50 euros/pax</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-blue-600">55,00 euros/pax</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Margen bruto</p>
                <p className="text-2xl font-bold text-green-600">30,0%</p>
                <p className="text-xs text-green-600 font-medium">1.320,00 euros</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10">
              <p className="text-amber-800 font-semibold mb-1">Atencion: sobreproduccion en cocteles</p>
              <p className="text-slate-700 text-sm">
                En un coctel standing, el consumo individual varia entre un 30% y un 50% respecto a la media. Algunos invitados comen 3 canapes y otros 12. La sobreproduccion del 15% es un minimo: si el cliente es exigente o el evento es largo (+3 horas), sube al 20%. <strong>Nunca presupuestes un coctel sin margen de sobreproduccion</strong> o te quedaras corto y la imagen de tu empresa se resiente.
              </p>
            </div>

            {/* Section 6: Escalar recetas */}
            <h2 id="escalar-recetas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Scale className="w-7 h-7 text-indigo-500" /> Como Escalar Recetas para Grandes Volumenes</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Uno de los mayores errores del catering es pensar que escalar una receta de 4 a 100 raciones es multiplicar por 25. <strong>No lo es.</strong> Al trabajar con grandes volumenes, entran en juego factores que cambian el coste y el resultado:
            </p>

            <div className="space-y-4 mb-8">
              {[
                { title: 'Especias y sal: factor de correccion x0,8', desc: 'Las especias y la sal no escalan linealmente. Para 100 raciones, usa el 80% de lo que daria la multiplicacion directa. Ejemplo: si tu receta de 4 raciones lleva 8 g de sal, para 100 raciones no uses 200 g sino 160 g.' },
                { title: 'Tiempos de coccion: x1,5 a x2', desc: 'Un guiso para 4 personas tarda 90 minutos. El mismo guiso para 100 personas puede tardar 150-180 minutos en ollas industriales. Esto afecta a la planificacion de produccion y al coste energetico.' },
                { title: 'Mermas: aumentan un 3-5%', desc: 'A mayor volumen, mas manipulacion, mas trasvases y mas perdida. Una merma del 10% en una receta para 4 puede ser del 13-15% para 100. Incluye este ajuste en el escandallo.' },
                { title: 'Precios: negociables por volumen', desc: 'Comprar 18 kg de solomillo te da poder de negociacion. Un descuento del 5-10% en materia prima por volumen puede ahorrar 200-500 euros por evento. Negocia siempre.' },
              ].map(({ title, desc }) => (
                <div key={title} className="border-l-4 border-indigo-400 pl-5">
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{title}</h3>
                  <p className="text-slate-700 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-10">
              <p className="text-indigo-800 font-semibold mb-2">Escalador automatico de PLEKO</p>
              <p className="text-slate-700 text-sm">
                PLEKO incluye un <strong>escalador de raciones</strong> que ajusta automaticamente las cantidades de tu receta al numero de comensales del evento. Introduce la receta para 4 raciones, selecciona 100, y el sistema calcula cantidades, costes y lista de compras. Ahorra horas de calculos manuales en cada presupuesto. <Link to="/register" className="text-indigo-600 hover:underline">Prueba gratis 14 dias</Link>.
              </p>
            </div>

            {/* Section 7: Costes ocultos */}
            <h2 id="costes-ocultos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><AlertTriangle className="w-7 h-7 text-red-500" /> Costes Ocultos del Catering</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Los costes ocultos son la razon por la que muchos caterings novatos pierden dinero en sus primeros eventos. Si no los incluyes en el presupuesto, se comen tu margen silenciosamente:
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-red-800 text-white">
                    <th className="p-3 text-left font-medium">Coste oculto</th>
                    <th className="p-3 text-center font-medium">Impacto tipico</th>
                    <th className="p-3 text-left font-medium">Como prevenirlo</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Transporte refrigerado', '300-800 euros/evento', 'Incluye siempre en presupuesto. Calcula 0,80-1,50 euros/km ida y vuelta.'],
                    ['Montaje y desmontaje', '4-8 horas de equipo', 'Cobra como partida separada o incluye en precio/persona (4-8 euros/pax).'],
                    ['Sobreproduccion planificada', '10-15% del coste MP', 'Incluye como linea en el escandallo. No es desperdicio: es seguro.'],
                    ['Merma por transporte', '3-5% adicional', 'Envasa correctamente, usa contenedores adecuados, cadena de frio.'],
                    ['Personal extra no previsto', '150-200 euros/persona', 'Presupuesta siempre 1-2 extras. Mejor sobrar que quedarse corto.'],
                    ['Roturas de material', '2-3% del alquiler', 'Incluye seguro de roturas en el presupuesto de material.'],
                    ['Horas extra del equipo', '15-25 euros/hora', 'Presupuesta 1-2 horas extra por evento para imprevistos.'],
                    ['Limpieza post-evento', '200-400 euros', 'Incluye siempre. El cliente espera que dejes el espacio como lo encontraste.'],
                  ].map(([coste, impacto, prevencion], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{coste}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-red-600">{impacto}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{prevencion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section 8: Food cost objetivo */}
            <h2 id="food-cost-objetivo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><PieChart className="w-7 h-7 text-indigo-500" /> Food Cost Objetivo en Catering</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En catering hay <strong>dos food costs que debes controlar</strong>: el de materia prima y el total (incluyendo todas las partidas). Ambos son igualmente importantes, pero se miden de forma diferente:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                <p className="text-indigo-800 font-bold text-lg mb-2">Food cost materia prima</p>
                <p className="text-4xl font-bold text-indigo-600 mb-2">25-30%</p>
                <p className="text-slate-700 text-sm">Solo ingredientes, bebidas y sobreproduccion. Comparable al food cost de un restaurante. Si esta por encima del 30%, la receta es demasiado costosa para el PVP o los precios de compra son altos.</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                <p className="text-slate-800 font-bold text-lg mb-2">Food cost total (con operativa)</p>
                <p className="text-4xl font-bold text-slate-700 mb-2">45-55%</p>
                <p className="text-slate-700 text-sm">Incluye personal, logistica, material e imprevistos. El margen bruto resultante (45-55%) debe cubrir costes fijos, administracion y beneficio neto. Si supera el 60%, el evento no es rentable.</p>
              </div>
            </div>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de evento</th>
                    <th className="p-3 text-center font-medium">FC materia prima</th>
                    <th className="p-3 text-center font-medium">FC total</th>
                    <th className="p-3 text-center font-medium">Margen habitual</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Coffee break / desayuno', '20-25%', '40-48%', 'Alto', 'bg-green-50'],
                    ['Cocktail / aperitivo', '25-30%', '48-55%', 'Medio-alto', 'bg-green-50'],
                    ['Almuerzo / cena sentados', '28-33%', '50-58%', 'Medio', 'bg-yellow-50'],
                    ['Boda premium', '30-35%', '55-65%', 'Medio-bajo', 'bg-yellow-50'],
                    ['Evento institucional (hospital, colegio)', '22-28%', '45-52%', 'Alto', 'bg-green-50'],
                  ].map(([tipo, fcMp, fcTotal, margen, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-indigo-700">{fcMp}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-700">{fcTotal}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{margen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section 9: Precio por persona */}
            <h2 id="precio-persona" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Como Fijar el Precio por Persona con Margen Garantizado</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Muchos caterers fijan precios "a ojo" basandose en lo que cobra la competencia. Eso es un error. El precio correcto se calcula <strong>de abajo hacia arriba</strong>: primero conoces tu coste real y luego aplicas el margen que necesitas.
            </p>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 mb-6">
              <p className="text-indigo-800 font-bold text-lg mb-3">Formula de precio por persona</p>
              <p className="text-2xl font-mono font-bold text-indigo-700 text-center mb-4">
                PVP = Coste total por persona / (1 - Margen deseado)
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-sm text-slate-600">Coste total/pax: 42 euros</p>
                  <p className="text-sm text-slate-600">Margen: 25%</p>
                  <p className="text-lg font-bold text-indigo-600">PVP = 56 euros</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-600">Coste total/pax: 42 euros</p>
                  <p className="text-sm text-slate-600">Margen: 30%</p>
                  <p className="text-lg font-bold text-indigo-600">PVP = 60 euros</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-600">Coste total/pax: 42 euros</p>
                  <p className="text-sm text-slate-600">Margen: 35%</p>
                  <p className="text-lg font-bold text-indigo-600">PVP = 64,62 euros</p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-800 mb-4">5 reglas para fijar precios en catering</h3>
            <ul className="space-y-3 mb-10">
              {[
                'Nunca fijes el precio antes de calcular el coste real completo (todas las partidas, no solo materia prima)',
                'Aplica un minimo del 25% de margen bruto. Por debajo, no cubres costes fijos ni imprevistos',
                'Para eventos premium (bodas, galas), el margen debe ser del 30-35% porque el riesgo operativo es mayor',
                'Cobra siempre un minimo por evento, independientemente del numero de personas (para cubrir costes fijos de logistica)',
                'Revisa los precios de materia prima antes de cada presupuesto: un cambio del 10% en el solomillo puede eliminar tu margen',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {/* Section 10: Software vs Excel */}
            <h2 id="software-vs-excel" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Software vs Excel para Gestionar Costes de Catering</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Muchos caterings empiezan con Excel para calcular presupuestos. Funciona cuando haces 2-3 eventos al mes. Pero cuando creces, Excel se convierte en un cuello de botella que consume horas y genera errores:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Aspecto</th>
                    <th className="p-3 text-center font-medium">Excel</th>
                    <th className="p-3 text-center font-medium">Software (PLEKO)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Escalar recetas', 'Manual (formulas propensas a error)', 'Automatico con 1 clic'],
                    ['Actualizar precios', 'Celda por celda en cada hoja', 'Un precio cambia y actualiza todas las recetas'],
                    ['Escandallo de evento', '1-2 horas por presupuesto', '10-15 minutos'],
                    ['Control de mermas', 'Dificil de mantener actualizado', 'Integrado en cada ingrediente'],
                    ['Historial de precios', 'No existe o es caótico', 'Automatico con cada factura escaneada'],
                    ['Colaboracion en equipo', 'Versiones de archivo, conflictos', 'Tiempo real, roles y permisos'],
                    ['OCR de facturas', 'Introduccion manual', 'Escanea el albaran y actualiza precios'],
                    ['Lista de compras por evento', 'Calculo manual', 'Generada automaticamente desde el menu'],
                    ['Coste', 'Gratis (pero consume horas)', 'Desde 49 euros/mes (ahorra horas)'],
                  ].map(([aspecto, excel, software], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{aspecto}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{excel}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-indigo-700 font-medium">{software}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              La transicion de Excel a software es el paso que separa un catering artesanal de uno profesional. Si preparas mas de 4-5 presupuestos al mes, un <Link to="/software-escandallo" className="text-indigo-600 hover:underline">software de escandallos</Link> se amortiza en el primer mes solo en ahorro de tiempo. Y si aun prefieres Excel, puedes empezar con nuestra <Link to="/plantilla-escandallo-excel" className="text-indigo-600 hover:underline">plantilla de escandallo gratuita</Link>.
            </p>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre escandallos de catering</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-indigo-500" /> Guias relacionadas</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Escandallo en hosteleria: guia completa' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas y cocteles' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/software-escandallo', label: 'Software de escandallo para restaurantes' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: tabla completa' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-2xl p-8 text-center text-white">
            <Truck className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Calcula los escandallos de tus eventos automaticamente</h2>
            <p className="text-indigo-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automaticamente el coste de todas tus recetas, escala ingredientes al numero de comensales, escanea facturas con IA y te genera la lista de compras para cada evento. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-indigo-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
